import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { scheduleProactiveTokenRefresh } from './services/apiClient';

// ── Chunk load error recovery ────────────────────────────────────────────────
// After a frontend deployment, asset hashes change (e.g. securityPolicy-C1eJZZBg.js
// becomes securityPolicy-X9aB2kLp.js). Users or CDN caches with the old HTML
// try to lazy-load chunks that no longer exist → TypeError. A single page reload
// fetches the new HTML with correct chunk references.
//
// Two handlers cover all code-splitting failure modes:
//   1. vite:preloadError  — Vite's <link rel="modulepreload"> tags (CSS/JS deps)
//   2. unhandledrejection — React.lazy() dynamic import() failures
//
// The sessionStorage guard prevents infinite reload loops: if the reload itself
// fails (e.g. the server is actually down), we don't keep reloading.
// ─────────────────────────────────────────────────────────────────────────────

const CHUNK_RELOAD_KEY = 'hipotech-chunk-reload';

function handleChunkLoadError() {
  if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    window.location.reload();
  }
}

// Vite modulepreload failures (CSS/JS dependency preloads via <link> tags)
window.addEventListener('vite:preloadError', handleChunkLoadError);

// React.lazy() / dynamic import() failures — the TypeError bubbles up as
// an unhandled promise rejection with a message containing "dynamically imported module"
window.addEventListener('unhandledrejection', (event) => {
  const msg = String(event.reason?.message || event.reason || '');
  if (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Loading chunk')
  ) {
    handleChunkLoadError();
  }
});

// Clear the reload guard on successful page load — if we get here, the reload
// worked and the next deploy should trigger a fresh reload cycle.
// Use requestIdleCallback to avoid blocking first paint.
if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
  const clear = () => sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  if ('requestIdleCallback' in window) {
    requestIdleCallback(clear);
  } else {
    setTimeout(clear, 3000);
  }
}

// ── Token refresh ────────────────────────────────────────────────────────────
// On page load, if an access token is already stored (returning user / page refresh),
// re-arm the proactive refresh scheduler so the user stays logged in indefinitely.
const existingToken = localStorage.getItem('access_token');
if (existingToken) {
  scheduleProactiveTokenRefresh(existingToken);
}

// ── React mount ──────────────────────────────────────────────────────────────

const rootElement = document.getElementById('root') as HTMLElement;

if (rootElement.hasChildNodes()) {
  // ── Pre-rendered by react-snap — hydrate ──────────────────────────────
  // react-snap pre-renders public pages at build time. When the browser
  // loads the pre-rendered HTML, React hydrates it (attaches event handlers
  // to the existing DOM). Mismatches are expected and harmless:
  //   - Auth state differs (react-snap renders logged-out, user may be logged-in)
  //   - Environment differs (react-snap at localhost:3000, production at hipotech.net)
  //   - Suspense boundaries resolve differently (lazy-loaded chunks)
  // React recovers by falling back to client-side rendering for the mismatched
  // subtree. The onRecoverableError callback suppresses the console noise.
  ReactDOM.hydrateRoot(rootElement, <App />, {
    onRecoverableError: (error: unknown) => {
      // Suppress hydration mismatch warnings in production — they're expected.
      // In development, log them so developers can investigate if needed.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[hydration]', error);
      }
    },
  });
} else {
  // ── No pre-rendered content — standard client render ──────────────────
  // Admin pages, authenticated pages, and any page react-snap didn't
  // pre-render use createRoot with no hydration step.
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}
